import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

/**
 * Trap Tab inside a container while it is open, and give focus back on close.
 *
 * This is the part of a dialog that is always skipped and always noticed. Without
 * it, Tab walks out of the modal into the page behind, which for a keyboard or
 * screen-reader user means the modal has effectively vanished while still blocking
 * everything. Returning focus to the trigger on close is the other half: without it
 * focus resets to the top of the document and the user loses their place.
 */
/**
 * Where focus should land when a dialog opens.
 *
 * The first focusable element is almost always the close button, because it sits at the
 * top right of the panel. Landing there means the very first Space or Enter dismisses the
 * dialog -- so somebody who opens a form, starts typing and hits Enter watches their work
 * disappear, with no clue what they did wrong. Reported as "typing selects the cross and
 * closes the window", which is exactly what it is.
 *
 * So a field wins over a button. If the panel has no field it is a confirmation rather
 * than a form, and the first focusable element is the right answer there: the choice IS
 * the buttons.
 *
 * A dialog that wants something else still passes `initialFocusRef`, which takes
 * precedence over all of this.
 */
function preferredInitialFocus(container: HTMLElement): HTMLElement | undefined {
  const focusable = focusableWithin(container);
  const field = focusable.find((element) =>
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName),
  );
  return field ?? focusable[0];
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  options: { initialFocusRef?: RefObject<HTMLElement | null>; onEscape?: () => void } = {},
): void {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const { initialFocusRef, onEscape } = options;
  // The latest Escape handler, read at key time. Keeping it out of the effect's
  // dependencies matters: callers usually pass a new function on every render, and
  // re-running the effect moved focus back to the first field while the person typed
  // in another one.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    if (!container) return;

    const target =
      initialFocusRef?.current ?? preferredInitialFocus(container) ?? container;
    // Focus on the next frame so the element exists and any entry animation has
    // begun; focusing during the same tick fights the browser's own scroll anchor.
    const raf = requestAnimationFrame(() => target.focus({ preventScroll: true }));

    const onKeyDown = (event: KeyboardEvent) => {
      const escape = onEscapeRef.current;
      if (event.key === 'Escape' && escape) {
        event.stopPropagation();
        escape();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === first || activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus?.({ preventScroll: true });
    };
  }, [active, containerRef, initialFocusRef]);
}

/** Stop the page behind an overlay from scrolling while it is open. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}
